import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { api } from '../services/api';

interface Card_ {
  id: string;
  creditAccountId: string;
  nickname: string;
  issuer?: string;
  last4?: string;
  maskedPattern?: string;
  notes?: string;
}

interface Account {
  id: string;
  name: string;
  type: string;
}

export default function CardsPage() {
  const [cards, setCards] = useState<Card_[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Card_ | null>(null);
  const [form, setForm] = useState({
    nickname: '',
    creditAccountId: '',
    issuer: '',
    last4: '',
    maskedPattern: '',
    notes: '',
  });

  const load = () => {
    api.get<Card_[]>('/api/cards').then(setCards);
    api.get<Account[]>('/api/accounts').then(setAccounts);
  };

  useEffect(() => { load(); }, []);

  const creditAccounts = accounts.filter((a) => a.type === 'credit');

  const openNew = () => {
    setEditing(null);
    setForm({ nickname: '', creditAccountId: creditAccounts[0]?.id || '', issuer: '', last4: '', maskedPattern: '', notes: '' });
    setDialogOpen(true);
  };

  const openEdit = (card: Card_) => {
    setEditing(card);
    setForm({
      nickname: card.nickname,
      creditAccountId: card.creditAccountId,
      issuer: card.issuer || '',
      last4: card.last4 || '',
      maskedPattern: card.maskedPattern || '',
      notes: card.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const body = {
      nickname: form.nickname,
      creditAccountId: form.creditAccountId,
      issuer: form.issuer || undefined,
      last4: form.last4 || undefined,
      maskedPattern: form.maskedPattern || undefined,
      notes: form.notes || undefined,
    };
    if (editing) {
      await api.put(`/api/cards/${editing.id}`, body);
    } else {
      await api.post('/api/cards', body);
    }
    setDialogOpen(false);
    load();
  };

  const handleDelete = async (id: string) => {
    if (confirm('Poistetaanko tämä kortti?')) {
      await api.delete(`/api/cards/${id}`);
      load();
    }
  };

  const getAccountName = (id: string) => accounts.find((a) => a.id === id)?.name || id;

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={600} sx={{ flexGrow: 1 }}>
          Kortit
        </Typography>
        <Button startIcon={<AddIcon />} variant="contained" onClick={openNew}>
          Lisää
        </Button>
      </Box>

      {creditAccounts.length === 0 && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Luo ensin luottotili, jotta voit lisätä kortteja.
        </Typography>
      )}

      {cards.map((card) => (
        <Card key={card.id} sx={{ mb: 1.5 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box display="flex" alignItems="center">
              <CreditCardIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Box sx={{ flexGrow: 1 }}>
                <Typography fontWeight={600}>{card.nickname}</Typography>
                <Box display="flex" gap={0.5} flexWrap="wrap">
                  {card.last4 && <Chip label={`****${card.last4}`} size="small" />}
                  {card.issuer && <Chip label={card.issuer} size="small" variant="outlined" />}
                  <Chip label={getAccountName(card.creditAccountId)} size="small" color="primary" variant="outlined" />
                </Box>
              </Box>
              <IconButton size="small" onClick={() => openEdit(card)}>
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => handleDelete(card.id)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </CardContent>
        </Card>
      ))}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editing ? 'Muokkaa korttia' : 'Uusi kortti'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Kutsumanimi"
              value={form.nickname}
              onChange={(e) => setForm({ ...form, nickname: e.target.value })}
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel>Luottotili</InputLabel>
              <Select
                value={form.creditAccountId}
                label="Luottotili"
                onChange={(e) => setForm({ ...form, creditAccountId: e.target.value })}
              >
                {creditAccounts.map((acc) => (
                  <MenuItem key={acc.id} value={acc.id}>
                    {acc.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Kortin myöntäjä (valinnainen)"
              value={form.issuer}
              onChange={(e) => setForm({ ...form, issuer: e.target.value })}
              fullWidth
            />
            <TextField
              label="Viimeiset 4 numeroa (valinnainen)"
              value={form.last4}
              onChange={(e) => setForm({ ...form, last4: e.target.value })}
              fullWidth
              inputProps={{ maxLength: 4 }}
            />
            <TextField
              label="Peitetty korttinumero (valinnainen)"
              value={form.maskedPattern}
              onChange={(e) => setForm({ ...form, maskedPattern: e.target.value })}
              fullWidth
              placeholder="esim. 401046******2708"
            />
            <TextField
              label="Muistiinpanot (valinnainen)"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Peruuta</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!form.nickname || !form.creditAccountId}
          >
            Tallenna
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
